/**
 * Modal anchored over the target cell, showing miniature versions of
 * the actual cards present on that location that are valid attack
 * targets. Each mini card carries the same overlays as its board
 * counterpart (ATK/DEF chips, status badges, equipment stack; see
 * BoardCardOverlays). Hovering a mini card reveals a large preview of
 * the same card.
 *
 * Click a mini card to resolve the attack. Click the backdrop to
 * abandon (move portion still applies if this came from a Move-and-
 * Attack).
 *
 * v1.43. Phase 3a of combat.
 * v1.43a. Viewport-aware placement (above/below/horizontal clamp).
 * v1.43b. Mini-card + hover-preview UX. Codex-correct Airborne gate
 *          handled by getValidAttackTargets in engine/core/combat.js;
 *          burrowed / submerged targets are already filtered out
 *          before reaching this component.
 *
 * Props:
 *   modal. {
 *     cellRect: { left, right, top, bottom },     viewport px
 *     targets: [{
 *       kind: 'card' | 'site',
 *       card: cardInstance,
 *       fullCard: catalog entry (for imageUrl/name/stats),
 *       effectiveStatuses: string[],
 *       statChips: [{ stat, value, delta }]  (equipActions.displayStatChips)
 *     }],
 *     attackerId, attackerName,
 *   } or null/undefined → renders nothing
 *   viewScale, onPick, onCancel.
 *   logbookOpen. reserves the chat panel's left gutter during placement.
 */
import { useEffect, useRef, useState, useLayoutEffect } from 'preact/hooks';
import { motion, AnimatePresence } from 'framer-motion';
import { ACCENT_GOLD, GOLD, RADIUS } from '../../lib/medievalTheme';
import FoilCanvas from '../FoilCanvas';
import { resolveLocalImageUrl } from '../../utils/localApi';
import { isFoilFinish, resolvePrinting } from '../../utils/sorcery/foil.js';
import { foilZonesForCard } from '../../utils/sorcery/foilZones.js';
import { DEFAULT_FOIL_PARAMS } from '../../utils/sorcery/foilParams.js';
import { setHoveredCard } from '../../utils/game/cardZoomBus';
import StoneButton from './StoneButton';
import BoardCardOverlays, { STONE_BG, STONE_BLEND } from './BoardCardOverlays';
import { useEscDismiss } from './useEscDismiss';
import { t } from '../../localization/t';

const Z              = 1100;
const Z_PREVIEW      = 1101;
const GAP_CQW        = 0.55;
const EDGE_PAD_PX    = 16;
const CELL_GAP_PX    = 12;
const CANCEL_HEIGHT_CQW = 2.2;

// Left gutter the logbook/chat panel occupies when expanded: its
// `left-3` offset (0.75cqw at --spacing: 0.25cqw) plus its 15cqw open
// width. Reserved during placement so no candidate tile ever lands on
// the chat box, where a click would read as picking that target.
const LOGBOOK_GUTTER_CQW = 15.75;

// Mini card sizes (cqw). Portrait for minions/avatars, landscape for
// sites. The wider/shorter footprint matches how sites render on the
// playmat.
const MINI_PORTRAIT_W_CQW  = 5.5;
const MINI_PORTRAIT_H_CQW  = 7.5;
const MINI_LANDSCAPE_W_CQW = 7.5;
const MINI_LANDSCAPE_H_CQW = 5.5;

// Hover-preview sizes (cqw). Match EffectPromptDrawer's card preview
// dimensions so the visual language stays consistent.
const PREVIEW_PORTRAIT_W_CQW  = 14;
const PREVIEW_PORTRAIT_H_CQW  = 19;
const PREVIEW_LANDSCAPE_W_CQW = 19;
const PREVIEW_LANDSCAPE_H_CQW = 14;

const OPAQUE_BASE = '#0e0c08';

const PANEL_SHADOW = [
  'inset 0 0.0625cqw 0 rgba(255, 235, 195, 0.18)',
  'inset 0 -0.0625cqw 0 rgba(0, 0, 0, 0.45)',
  'inset 0 0 1.5cqw rgba(0, 0, 0, 0.4)',
  '0 0 0 0.0625cqw rgba(180, 140, 60, 0.55)',
  '0 0 0 0.1875cqw rgba(0, 0, 0, 0.55)',
  '0 0.4cqw 1cqw rgba(0, 0, 0, 0.6)',
].join(', ');

const MINI_SHADOW_IDLE = '0 0.35cqw 0.9cqw rgba(0,0,0,0.6), inset 0 0 0 1px rgba(212, 168, 67, 0.4)';
const MINI_SHADOW_HOVER = '0 0.5cqw 1.4cqw rgba(0,0,0,0.7), inset 0 0 0 1px rgba(255, 220, 130, 1), 0 0 1.4cqw rgba(232, 195, 110, 0.35)';

// ── public API ───────────────────────────────────────────────────

export default function AttackTargetModal({ modal, viewScale, logbookOpen, onPick, onCancel }) {
  return (
    <AnimatePresence>
      {modal && (
        <ModalContent
          key={modal.attackerId}
          modal={modal}
          viewScale={viewScale}
          logbookOpen={logbookOpen}
          onPick={onPick}
          onCancel={onCancel}
        />
      )}
    </AnimatePresence>
  );
}

// ── placement helpers ───────────────────────────────────────────
//
// The modal and the preview render `position: fixed` inside
// `.app-canvas`, whose translateZ(0) makes it the containing block.
// CSS left/top are measured from the CANVAS origin, not the viewport.
// All math below runs in viewport px (what getBoundingClientRect
// speaks), then subtracts the canvas origin at the end. Clamping uses
// the canvas rect too so nothing crosses the letterbox bars.

function appCanvasRect() {
  return document.querySelector('.app-canvas')?.getBoundingClientRect()
    || { left: 0, top: 0, right: window.innerWidth, bottom: window.innerHeight, width: window.innerWidth, height: window.innerHeight };
}

export function placeModal(cellRect, modalW, modalH, canvasRect, logbookOpen = false) {
  const cqw = (canvasRect.width || 0) / 100;
  const gutter = logbookOpen ? LOGBOOK_GUTTER_CQW * cqw : 0;
  const cLeft = canvasRect.left + EDGE_PAD_PX + gutter;
  const cRight = canvasRect.right - EDGE_PAD_PX;
  const cTop = canvasRect.top + EDGE_PAD_PX;
  const cBottom = canvasRect.bottom - EDGE_PAD_PX;
  const cellCenterX = (cellRect.left + cellRect.right) / 2;
  let left = cellCenterX - modalW / 2;
  let top = cellRect.top - modalH - CELL_GAP_PX;
  let placement = 'above';
  if (top < cTop) {
    placement = 'below';
    top = cellRect.bottom + CELL_GAP_PX;
    if (top + modalH > cBottom) {
      top = Math.max(cTop, cBottom - modalH);
    }
  }
  if (left < cLeft) left = cLeft;
  if (left + modalW > cRight) left = cRight - modalW;
  return { left, top, placement };
}

// Position the hover preview next to the modal, vertically centered on
// the hovered entry (`anchorRect`, the mini card's own rect). Left of
// the modal by default, right when the left side can't fit it. The
// vertical clamp keeps the preview fully on-canvas when the modal sits
// at the very top or bottom of the screen while staying as close to
// the hovered entry as the clamp allows.
function placePreview(modalRect, anchorRect, previewW, previewH, canvasRect) {
  const PAD = EDGE_PAD_PX;
  const GAP = 10;
  const cLeft = canvasRect.left + PAD;
  const cRight = canvasRect.right - PAD;
  const cTop = canvasRect.top + PAD;
  const cBottom = canvasRect.bottom - PAD;

  const anchorCenterY = (anchorRect.top + anchorRect.bottom) / 2;
  let top = anchorCenterY - previewH / 2;
  if (top + previewH > cBottom) top = cBottom - previewH;
  if (top < cTop) top = cTop;

  // Left of the modal first.
  let left = modalRect.left - GAP - previewW;
  if (left >= cLeft) return { left, top };
  // Then right.
  left = modalRect.right + GAP;
  if (left + previewW <= cRight) return { left, top };
  // Neither side fits: pin inside the canvas on the left edge.
  return { left: cLeft, top };
}

// ── card-image helpers ──────────────────────────────────────────

function cardImageBox(target, sizes) {
  const isSite = !!target.card?.isSite;
  const w = isSite ? sizes.landscapeW : sizes.portraitW;
  const h = isSite ? sizes.landscapeH : sizes.portraitH;
  return { w, h, isSite };
}

/**
 * The live instance's art wins. `printings[0]` is catalog order, not the
 * printing that was played: for tokens like Skeleton a promo sorts first,
 * so the prompt would show art the board never rendered. `card.imageUrl`
 * is the same field cardMesh.js draws from.
 */
export function resolveTargetImageUrl(target) {
  const printing = target?.card?.cardId
    ? resolvePrinting(target.fullCard, target.card.printingId, target.card.foiling || 'S')
    : null;
  return resolveLocalImageUrl(
    target?.card?.imageUrl
    || printing?.image_url
    || target?.fullCard?.printings?.[0]?.image_url
    || '',
  );
}

export function carriedCards(target) {
  return target?.card?.equippedCards || [];
}

export function targetAriaLabel(target) {
  const name = target?.card?.name || (target?.kind === 'site' ? t('site') : t('target'));
  const carried = carriedCards(target).map((eq) => eq.name).filter(Boolean);
  return carried.length
    ? t('Attack {name} carrying {carried}', { name, carried: carried.join(', ') })
    : t('Attack {name}', { name });
}

function CardImage({ target, sizes, hovered = false, miniShadows = true }) {
  const { w, h, isSite } = cardImageBox(target, sizes);
  const imageUrl = resolveTargetImageUrl(target);
  const cardName = target.fullCard?.name || target.card?.name || '';
  const foiling = target.card?.foiling || null;
  // Foil zone bevels apply to non-site cards; sites render landscape.
  const foilIsSite = isSite || !!target.card?.played_horizontally || target.fullCard?.type === 'Site';

  const overlays = (
    <BoardCardOverlays
      card={target.card}
      statuses={target.effectiveStatuses}
      chips={target.statChips}
      w={w}
      h={h}
      isSite={isSite}
    />
  );

  const wrapStyle = {
    width:  `${w}cqw`,
    height: `${h}cqw`,
    borderRadius: isSite ? RADIUS.cardLandscape : RADIUS.card,
    overflow: 'hidden',
    backgroundColor: OPAQUE_BASE,
    boxShadow: miniShadows ? (hovered ? MINI_SHADOW_HOVER : MINI_SHADOW_IDLE) : `0 0.5cqw 1.5cqw rgba(0,0,0,0.7)`,
    position: 'relative',
    transition: 'box-shadow 120ms ease-out, transform 120ms ease-out',
    transform: hovered ? 'translateY(-0.15cqw)' : 'translateY(0)',
  };

  if (!imageUrl) {
    return (
      <div style={wrapStyle}>
        <div style={{
          width: '100%', height: '100%', display: 'flex',
          alignItems: 'center', justifyContent: 'center',
          color: GOLD, fontSize: '0.85cqw', textAlign: 'center', padding: '0.3cqw',
        }}>{cardName}</div>
        {overlays}
      </div>
    );
  }

  if (isSite) {
    // Site image is shipped portrait; rotate 90° to render landscape.
    return (
      <div style={wrapStyle}>
        <img
          src={imageUrl}
          alt={cardName}
          draggable={false}
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            width: `${h}cqw`,
            height: `${w}cqw`,
            transform: 'translate(-50%, -50%) rotate(90deg)',
            transformOrigin: 'center center',
            objectFit: 'cover',
            pointerEvents: 'none',
          }}
        />
        {isFoilFinish(foiling) ? (
          <FoilCanvas imageUrl={imageUrl} zones={foilZonesForCard({ isSite: foilIsSite })} params={DEFAULT_FOIL_PARAMS} scrollSweep={false} rotate90 />
        ) : null}
        {overlays}
      </div>
    );
  }

  return (
    <div style={wrapStyle}>
      <img
        src={imageUrl}
        alt={cardName}
        draggable={false}
        style={{
          width: '100%', height: '100%',
          objectFit: 'cover',
          pointerEvents: 'none',
        }}
      />
      {isFoilFinish(foiling) ? (
        <FoilCanvas imageUrl={imageUrl} zones={foilZonesForCard({ isSite: foilIsSite })} params={DEFAULT_FOIL_PARAMS} scrollSweep={false} />
      ) : null}
      {overlays}
    </div>
  );
}

// ── modal content ───────────────────────────────────────────────

function ModalContent({ modal, viewScale, logbookOpen, onPick, onCancel }) {
  const { cellRect, targets, attackerName } = modal;
  const scale = viewScale || 1;
  const wrapRef = useRef(null);
  useEscDismiss(true, onCancel);
  const [pos, setPos] = useState({ left: 0, top: 0, placement: 'above', measured: false });
  // hovered = { idx, anchor } where anchor is the hovered mini card's
  // viewport rect, captured at mouseenter. The preview centers on it.
  const [hovered, setHovered] = useState(null);
  const [previewRect, setPreviewRect] = useState(null);
  const hoveredIdx = hovered?.idx ?? -1;

  useLayoutEffect(() => {
    if (!cellRect || !wrapRef.current) return;
    const r = wrapRef.current.getBoundingClientRect();
    const canvasRect = appCanvasRect();
    const placed = placeModal(cellRect, r.width, r.height, canvasRect, logbookOpen);
    setPos({
      left: (placed.left - canvasRect.left) / scale,
      top: (placed.top - canvasRect.top) / scale,
      placement: placed.placement,
      measured: true,
    });
  }, [cellRect?.left, cellRect?.right, cellRect?.top, cellRect?.bottom, targets?.length, scale, logbookOpen]);

  // The candidate tiles feed the alt-zoom bus so alt-hold enlarges the
  // hovered target. Clear it on unmount so a resolved attack doesn't
  // leave the last candidate stuck as the "hovered" card.
  useEffect(() => () => setHoveredCard(null), []);

  // Reposition the preview whenever the hovered entry changes. The
  // modal rect is re-measured LIVE here. Measuring it once at mount
  // would capture the pre-placement (0,0) rect and put the preview
  // somewhere unrelated. Preview size derives from the canvas width
  // (1cqw = canvasWidth / 100 viewport px, by definition of cqw).
  useLayoutEffect(() => {
    if (!hovered || !wrapRef.current) { setPreviewRect(null); return; }
    const t = targets[hovered.idx];
    if (!t) { setPreviewRect(null); return; }
    const modalRect = wrapRef.current.getBoundingClientRect();
    const canvasRect = appCanvasRect();
    const cqw = (canvasRect.width || window.innerWidth) / 100;
    const isSite = !!t.card?.isSite;
    const previewW = (isSite ? PREVIEW_LANDSCAPE_W_CQW : PREVIEW_PORTRAIT_W_CQW) * cqw;
    const previewH = (isSite ? PREVIEW_LANDSCAPE_H_CQW : PREVIEW_PORTRAIT_H_CQW) * cqw;
    const placed = placePreview(modalRect, hovered.anchor, previewW, previewH, canvasRect);
    setPreviewRect({
      left: (placed.left - canvasRect.left) / scale,
      top: (placed.top - canvasRect.top) / scale,
    });
  }, [hovered, targets, pos.measured, scale]);

  const cssLeft = pos.left;
  const cssTop  = pos.top;
  const hoveredTarget = hoveredIdx >= 0 ? targets[hoveredIdx] : null;

  return (
    <>
      {/* Click-outside backdrop. Only its OWN hits cancel: surfaces
          layered above it (the logbook/chat panel) bubble their
          mousedown through here, and swallowing those was what made
          the rest of the UI feel dead while the prompt was up. */}
      <div
        aria-hidden="true"
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: Z - 1,
          background: 'transparent',
        }}
        onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel(); }}
      />

      <motion.div
        ref={wrapRef}
        initial={{ opacity: 0, scale: 0.96, y: pos.placement === 'above' ? 6 : -6 }}
        animate={{ opacity: pos.measured ? 1 : 0, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.97, transition: { duration: 0.1 } }}
        transition={{ type: 'spring', stiffness: 600, damping: 32 }}
        role="dialog"
        aria-label={t('Pick attack target for {name}', { name: attackerName || t('unit') })}
        style={{
          position: 'fixed',
          left: cssLeft,
          top:  cssTop,
          zIndex: Z,
          padding: '0.85cqw 0.85cqw 1.4cqw 0.85cqw',
          borderRadius: 'var(--radius-xl)',
          backgroundColor: OPAQUE_BASE,
          backgroundImage: STONE_BG,
          backgroundBlendMode: STONE_BLEND,
          boxShadow: PANEL_SHADOW,
          pointerEvents: 'auto',
          visibility: pos.measured ? 'visible' : 'hidden',
        }}
      >
        {(!targets || targets.length === 0) ? (
          <div style={{
            color: ACCENT_GOLD,
            fontSize: '1cqw',
            padding: '1.2cqw 1.4cqw',
            textAlign: 'center',
            fontWeight: 700,
            letterSpacing: '0.04em',
          }}>
            {t('No valid targets.')}
          </div>
        ) : (
          <div style={{
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'wrap',
            gap: `${GAP_CQW * 1.4}cqw`,
            justifyContent: 'center',
            alignItems: 'center',
            maxWidth: '40cqw',
          }}>
            {targets.map((target, idx) => (
              <div
                key={target.card?.id || `t-${idx}`}
                onMouseEnter={(e) => {
                  setHovered({ idx, anchor: e.currentTarget.getBoundingClientRect() });
                  setHoveredCard(target.card);
                }}
                onMouseLeave={() => {
                  setHovered((h) => (h?.idx === idx ? null : h));
                  setHoveredCard(null);
                }}
                onClick={() => onPick(target)}
                role="button"
                aria-label={targetAriaLabel(target)}
                style={{ cursor: 'pointer', position: 'relative' }}
              >
                <CardImage
                  target={target}
                  sizes={{
                    portraitW:  MINI_PORTRAIT_W_CQW,
                    portraitH:  MINI_PORTRAIT_H_CQW,
                    landscapeW: MINI_LANDSCAPE_W_CQW,
                    landscapeH: MINI_LANDSCAPE_H_CQW,
                  }}
                  hovered={idx === hoveredIdx}
                />
                {/* v1.76. Region-transition badge: marks targets the
                    attacker can only reach by burrowing/submerging
                    first. Tells the player the strike will auto-
                    transition the attacker before damage resolves. */}
                {target.requiresRegionTransition ? (
                  <div
                    title={target.requiresRegionTransition === 'underground'
                      ? t('Attacker will burrow before striking')
                      : t('Attacker will submerge before striking')}
                    style={{
                      position: 'absolute',
                      top: '-0.4cqw',
                      right: '-0.4cqw',
                      width: '1.6cqw',
                      height: '1.6cqw',
                      borderRadius: '50%',
                      background: target.requiresRegionTransition === 'underground'
                        ? 'rgba(160, 100, 40, 0.92)'
                        : 'rgba(80, 160, 220, 0.92)',
                      border: '0.12cqw solid rgba(0,0,0,0.55)',
                      boxShadow: '0 0.2cqw 0.6cqw rgba(0,0,0,0.55)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: '#fff',
                      fontSize: '1.05cqw',
                      lineHeight: 1,
                      fontWeight: 700,
                      pointerEvents: 'none',
                    }}
                  >
                    ↓
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        )}
        {/* Explicit way out. The backdrop still cancels, but nothing
            told the player that, so a mis-drop felt like a dead end. */}
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '0.85cqw' }}>
          <StoneButton heightCqw={CANCEL_HEIGHT_CQW} onClick={onCancel}>
            {t('Cancel')}
          </StoneButton>
        </div>
      </motion.div>

      {/* Hover preview. Large card image + statuses + stat pills.
          Positioned to the side of the modal; falls back across sides
          if the natural side would clip. */}
      <AnimatePresence>
        {hoveredTarget && previewRect && (
          <motion.div
            key={hoveredTarget.card?.id || 'preview'}
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.97, transition: { duration: 0.12 } }}
            transition={{ type: 'spring', stiffness: 600, damping: 32 }}
            style={{
              position: 'fixed',
              left: previewRect.left,
              top:  previewRect.top,
              zIndex: Z_PREVIEW,
              pointerEvents: 'none',
            }}
          >
            <PreviewCard target={hoveredTarget} />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

function PreviewCard({ target }) {
  const carriedNames = carriedCards(target).map((eq) => eq.name).filter(Boolean);
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '0.6cqw',
    }}>
      <CardImage
        target={target}
        sizes={{
          portraitW:  PREVIEW_PORTRAIT_W_CQW,
          portraitH:  PREVIEW_PORTRAIT_H_CQW,
          landscapeW: PREVIEW_LANDSCAPE_W_CQW,
          landscapeH: PREVIEW_LANDSCAPE_H_CQW,
        }}
        miniShadows={false}
      />
      {carriedNames.length > 0 ? (
        <div style={{
          color: ACCENT_GOLD,
          fontSize: '0.85cqw',
          fontWeight: 700,
          letterSpacing: '0.03em',
          textAlign: 'center',
          textShadow: '0 1px 2px rgba(0,0,0,0.85)',
        }}>
          {t('Carrying {names}', { names: carriedNames.join(', ') })}
        </div>
      ) : null}
    </div>
  );
}
